import { Component, Input, Output, EventEmitter, OnInit, OnChanges, ViewChild, ElementRef, SimpleChange, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { SpacerService, SpacerBatchDto, SpacerTestsDto, CreateSpacerBatchRequest, SpacerLabSheetDto } from '../../../services/spacer.service';
import { forkJoin } from 'rxjs';
import { PrintService } from '../../../shared/print/print-dialog.component';
import { BatchDetailComponent } from './batch-detail/batch-detail.component';
import { SlurryStatuses } from '../../../services/spacer.service';  

interface TestItem {
  testInputMasterId: number;
  name: string;
  selected: boolean;
  type: 'compatibility' | 'qaqc' | 'ssst' | 'hpht';
}

enum SpacerHphtType {
  Fann77 = 1,
  Chann7600 = 2,
  Chann7550 = 3
}

enum SpacerCompatibilityRatioType {
  Mud = 1,
  Cement = 2,
  Spacer = 3
}

const HPHT_TYPE_NAMES: Record<number, string> = {
  [SpacerHphtType.Fann77]: 'Spacer HPHT Rheology (Fann 77)',
  [SpacerHphtType.Chann7600]: 'Spacer HPHT Rheology (Chann 7600)',
  [SpacerHphtType.Chann7550]: 'Spacer HPHT Rheology (Chann 7550)'
};

const TEST_NAMES = {
  compatibility: 'Spacer Compatibility',
  qaqc: 'Spacer QAQC',
  ssst: 'Spacer SSST'
} as const;

@Component({
  selector: 'app-batches',
  imports: [CommonModule, FormsModule, BatchDetailComponent],
  templateUrl: './batches.component.html',
  styleUrls: ['./batches.component.css']
})
export class BatchesComponent implements OnInit, OnChanges {
  @Input() spacerId: number | null = null;
  @Input() projectId: number | null = null;
  @Input() projectName: string = '';
  @Input() spacerName: string = '';
  @Input() spacerStatus: SlurryStatuses | null = null;
  @Input() projectStatus: string | null = null;
  @Output() batchDetailViewChange = new EventEmitter<boolean>();

  @ViewChild('labSheetContent') labSheetContent!: ElementRef<HTMLElement>;

  availableTests: TestItem[] = [];
  existingBatches: SpacerBatchDto[] = [];
  labSheetHtml = '';

  // Batch detail view state
  showBatchDetail: boolean = false;
  selectedBatch: SpacerBatchDto | null = null;
  batchTests: TestItem[] = [];
  isLoading: boolean = false;

  constructor(
    private spacerService: SpacerService,
    private printService: PrintService,
    private router: Router
  ) { }

  ngOnInit(): void { }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['spacerId'] || changes['spacerStatus']) && this.spacerId) {
      this.loadBatches();
    }
  }

  get hasTests(): boolean {
    return this.availableTests.length > 0;
  }

  get hasSelectedTests(): boolean {
    return this.availableTests.some(t => t.selected);
  }

  loadBatches(): void {
    if (!this.spacerId) return;

    this.isLoading = true;

    forkJoin({
      batches: this.spacerService.getBatches(this.spacerId),
      testInputs: this.spacerService.getTestInputs(this.spacerId)
    }).subscribe({
      next: (result) => {
        this.existingBatches = result.batches;
        this.processTestInputs(result.testInputs);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading batches data:', error);
        this.isLoading = false;
      }
    });
  }

  private getHphtName(typeId: number, index: number): string {
    const name =
      HPHT_TYPE_NAMES[typeId] ??
      'Spacer HPHT Rheology';

    return `${name} # ${index}`;
  }

  private convertRatioType(type: number | null | undefined): string {
    return type != null ? (SpacerCompatibilityRatioType[type] ?? 'Unknown') : 'Unknown';
  }

  private processTestInputs(testInputs: SpacerTestsDto): void {
    this.availableTests = [];

    this.testInputsData = testInputs;

    if (testInputs.spacerCompatibility) {
      this.availableTests.push({
        testInputMasterId: testInputs.spacerCompatibility.testInputMasterId,
        name: TEST_NAMES.compatibility,
        selected: false,
        type: 'compatibility'
      });
    }

    if (testInputs.spacerQaQc) {
      this.availableTests.push({
        testInputMasterId: testInputs.spacerQaQc.testInputMasterId,
        name: TEST_NAMES.qaqc,
        selected: false,
        type: 'qaqc'
      });
    }

    if (testInputs.spacerSsst) {
      this.availableTests.push({
        testInputMasterId: testInputs.spacerSsst.testInputMasterId,
        name: TEST_NAMES.ssst,
        selected: false,
        type: 'ssst'
      });
    }
    let fannCount = 0;
    let channCount = 0;

    (testInputs.spacerHpHtRheologyInputs ?? []).forEach((hpht) => {

      let currentNumber: number;

      switch (hpht.hpHtRheologyTypeId) {

        case SpacerHphtType.Fann77:
          currentNumber = ++fannCount;
          break;

        case SpacerHphtType.Chann7600:
        case SpacerHphtType.Chann7550:
          currentNumber = ++channCount;
          break;

        default:
          currentNumber = ++channCount;
          break;
      }

      this.availableTests.push({
        testInputMasterId: hpht.testInputMasterId,
        name: this.getHphtName(
          hpht.hpHtRheologyTypeId!,
          currentNumber
        ),
        selected: false,
        type: 'hpht'
      });

    });
  }

  private testInputsData: SpacerTestsDto | null = null;

  getTestNamesForBatch(batch: SpacerBatchDto): string {
    if (batch.tests.length === 0) {
      return '';
    }
    const testNames = batch.tests.map(test => {
      const availableTest = this.availableTests.find(t => t.testInputMasterId === test.testInputMasterId);
      return availableTest ? availableTest.name : `Test ${test.testInputMasterId}`;
    });
    return testNames.join('; ');
  }

  printAllTests(): void {
    if (!this.spacerId) {
      alert('No spacer selected');
      return;
    }
    this.printLabSheet(false);
  }

  printSelectedTests(): void {
    const selectedTests = this.availableTests.filter(t => t.selected);
    if (selectedTests.length === 0) {
      return;
    }
    this.printLabSheet(true);
  }

  private printLabSheet(printSelectedOnly: boolean): void {
    if (!this.spacerId) return;
    this.spacerService.getLabSheetData(this.spacerId).subscribe({
      next: (labSheetData) => {
        this.labSheetHtml = this.buildLabSheetHtml(printSelectedOnly, labSheetData);
        const contentEl =
          this.labSheetContent.nativeElement;

        contentEl.innerHTML =
          this.labSheetHtml;
        setTimeout(() => {
          const contentEl = this.labSheetContent.nativeElement;
          this.printService.print(contentEl, {
            windowWidth: window.screen.availWidth,
            windowHeight: window.screen.availHeight,
            bodyPadding: '8mm',
            pageSize: 'A4 portrait',
            pageMargin: '5mm',
            decorators: [
              {
                selector: '.lab-table',
                style: `
      width: 100% !important;
      border-collapse: collapse !important;
      table-layout: fixed !important;
      display: table !important;
      margin-top:4px !important;
      margin-bottom:8px !important;
  `
              },
              {
                selector: '.lab-table th',
                style: `
      border: 1px solid #000 !important;
      padding: 8px 4px !important;
      text-align:center !important;
      font-weight:bold !important;
      display: table-cell !important;
  `
              },
              {
                selector: '.lab-table td',
                style: `
      border: 1px solid #000 !important;
      padding: 6px 4px !important;
      display: table-cell !important;
  `
              },
              {
                selector: '.lab-table tr',
                style: `
      display: table-row !important;
  `
              }, {
                selector: 'table',
                style: `
    border-collapse: collapse !important;
    width: 100% !important;
    table-layout: fixed !important;
    display: table !important;
    font-family: GothamNarrowBook !important;
    font-size: 10px !important;
  `
              },
              {
                selector: 'tr',
                style: `
    display: table-row !important;
  `
              },
              {
                selector: 'td, th',
                style: `
    display: table-cell !important;
    padding: 4px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  `
              },
              {
                selector: '.materials-table',
                style: `
      width:100% !important;
      border:none !important;
      border-collapse:collapse !important;
      margin:0 !important;
      padding:0!important;
  `
              },
              {
                selector: '.materials-table th',
                style: `
      border:none !important;
      background:#DBDCDD !important;
      padding:2px 4px 2px 12px !important;
      font-weight:bold !important;
  `
              },
              {
                selector: '.materials-table td',
                style: `
      border:none !important;
      border-bottom:1px solid #DBDCDD !important;
      background:#FAAB18 !important;
      padding:2px 4px 2px 12px !important;
  `
              },
              {
                selector: '.materials-title',
                style: `
      background:#DBDCDD !important;
      text-align:left !important;
      font-weight:bold !important;
      border:none !important;
      padding:4px 0 4px 2px !important;
      margin:0 !important;
  `
              },
              {
                selector: '.materials-heading',
                style: `
      background:#DBDCDD !important;
      font-weight:bold !important;
  `
              },

              {
                selector: '.header-table',
                style: `
      border: 1px solid black !important;
      border-collapse: separate !important;
      border-spacing: 0 !important;
  `
              },
              {
                selector: '.header-table td',
                style: `
      border: none !important;
      padding: 5px !important;
  `
              },
              {
                selector: '.header-table, .header-table tr, .header-table td',
                style: `
      background: #ffffff !important;
      background-color: #ffffff !important;
  `
              },
              {
                selector: '.header-value',
                style: `
      text-decoration: underline !important;
      color:#000000 !important;
  `
              },
              {
                selector: '.qaqc-notes-label',
                style: `
      width:80px !important;
      min-width:80px !important;
      max-width:80px !important;

      text-align:left !important;
      vertical-align:middle !important;

      font-weight:normal !important;

      border-right:none !important;
      padding-left:8px !important;
  `
              },
              {
                selector: '.qaqc-notes-value',
                style: `
      border-left:none !important;
      vertical-align:top !important;
  `
              },
              {
                selector: '.qaqc-notes-highlight',
                style: `
      background:#B2CC34 !important;
      width:65% !important;
      padding:4px !important;
      white-space:pre-wrap !important;
      display:block !important;

      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
  `
              },
              {
                selector: '.mixability-table, .mixability-table td',
                style: `
      border-bottom:1px solid #000;
      padding:4px 0;
      margin:0 0 12px 0;
  `
              },
              {
                selector: '.notes-section',
                style: `
      border-bottom:1px solid #000;
      padding:4px 0;
      margin:0 0 12px 0;
  `
              },
              {
                selector: '.liquid-materials-separator',
                style: `
      border-bottom:1px solid #000;
      margin:0 0 12px 0;
  `
              },
              {
                selector: '.materials-top-line',
                style: `
      border-top:1px solid #000;
  `
              },
              {
                selector: 'hr',
                style: `
      border: 0 !important;
      border-top: 1px solid #000 !important;
      margin: 12px 0 !important;
      height: 0 !important;
  `
              },
              {
                selector: '.hpht-table',
                style: `
    width:auto !important;
table-layout:auto !important;
  `
              },
              {
  selector: '.report-print-table thead',
  style: `
    display: table-header-group !important;
  `
},
{
  selector: '.report-print-table',
  style: `
    width:100% !important;
    border-collapse:collapse !important;
  `
},

            ],
            fallbackBodyClass: 'lab-sheet-printing'
          });
        }, 100);
      },
      error: (error) => {
        console.error('Error loading lab sheet data:', error);
        alert('Failed to load lab sheet data. Please try again.');
      }
    });
  }

  private buildLabSheetHtml(printSelectedOnly: boolean, labSheetData: SpacerLabSheetDto): string {
    const testsToInclude = printSelectedOnly
      ? this.availableTests.filter(t => t.selected)
      : this.availableTests;

    if (testsToInclude.length === 0) {
      return '<p>No tests to print</p>';
    }

    const sentToLab =
      labSheetData.sentToLab
        ? new Date(
          labSheetData.sentToLab
        ).toLocaleDateString('en-US')
        : '';
let html = `
<table class="report-print-table" style="width:100%;">
  <thead>
    <tr>
      <td>
`;
    html += `
<table
class="header-table"
style="
width:100%;
margin-bottom:8px;
border:1px solid #000;
border-collapse:separate;
border-spacing:0;
">
`;
    html += '<tr>';
    html += `<td style="width:33%; padding:5px; "><strong>Project:</strong> <span class="header-value">${this.escapeHtml(labSheetData.projectName)}</span></td>`;
    html += `<td style="width:33%; padding:5px; "><strong>Slurry:</strong> <span class="header-value">${this.escapeHtml(labSheetData.slurryName)}</span></td>`;
    html += `<td style="width:33%; padding:5px;"><strong>Project By:</strong> <span class="header-value">${this.escapeHtml(labSheetData.projectBy)}</span></td>`;
    html += '</tr>';
    html += '<tr>';
    html += `<td style="padding: 5px;"><strong>Rig Name:</strong> <span class="header-value">${this.escapeHtml(labSheetData.rigName)}</span></td>`;
    html += `
<td colspan="2" style="padding:5px;">
<strong>Well Name:</strong> <span class="header-value">${this.escapeHtml(labSheetData.wellName)}</span>
</td>`;

    html += '</tr>';
    html += '<tr>';

    html += `
<td colspan="3" style="padding:5px;">
<strong>Job Type:</strong><span class="header-value"> ${this.escapeHtml(labSheetData.jobType)}</span>
${labSheetData.pipeSize
        ? `&nbsp;&nbsp;&nbsp;<strong>Pipe Size:</strong> <span class="header-value">${this.escapeHtml(labSheetData.pipeSize)}</span>`
        : ''
      }
</td>`;

    html += '</tr>';
    html += '</table>';
    html += `
      </td>
    </tr>
  </thead>

  <tbody>
    <tr>
      <td>
`;

    html += '<hr>';
    html += `<div>
Density:
<strong>${labSheetData.density.toFixed(2)}ppg</strong>
`;

    if (labSheetData.mudWeight != null) {
      html += `
  &nbsp;&nbsp;&nbsp;
  Mud Weight:
  <strong>${Number(labSheetData.mudWeight).toFixed(2)}ppg</strong>
  `;
    }

    html += `
&nbsp;&nbsp;&nbsp;
Sent To Lab:
<strong>${sentToLab}</strong>
</div>`;
    html += '<hr>';
    html += `
<div
style="
border-top:1px solid #000;
border-bottom:1px solid #000;
padding:0;
margin-bottom:10px;
">
`;
    html += `
<div class="materials-top-line"></div>`;
    html += '<table class="materials-table">';
    html += `
<colgroup>
  <col style="width:22%">
  <col style="width:10%">
  <col style="width:12%">
  <col style="width:16%">
  <col style="width:28%">
  <col style="width:12%">
</colgroup>`;
    html += '<thead>';

    html += `
<tr>
  <th colspan="6" class="materials-title">
    Dry Materials
  </th>
</tr>`;
    html += '<tr>';
    html += '<th style="width:22%; text-align:center;">Material Name</th>';
    html += '<th style="width:10%; text-align:center;">Percent (%)</th>';
    html += '<th style="width:12%; text-align:center;">Weight(lb)</th>';
    html += '<th style="width:16%; text-align:center;">Preferred Container</th>';
    html += '<th style="width:28%; text-align:center;">Container Location</th>';
    html += '<th style="width:12%; text-align:center;">Lab Weight</th>';
    html += '</tr>';
    html += '</thead>';

    html += '<tbody>';
    if (labSheetData.dryMaterials.length === 0) {
      html += '<tr><td colspan="6" style=" padding: 3px; text-align: center;">No dry materials</td></tr>';
    } else {
      labSheetData.dryMaterials.forEach(material => {
        html += '<tr>';
        html += `<td style="padding: 3px;">${this.escapeHtml(material.materialName)}</td>`;
        html += `<td style="padding: 3px; text-align: right;">
    ${material.percent ?? '-'}
</td>`;
        html += `
<td style=" padding: 3px; text-align: right;">
${(material.weight || 0).toFixed(3)}
</td>`;
        html += `<td style="padding: 3px;">${this.escapeHtml(material.preferredContainer || '')}</td>`;
        html += `<td style="padding: 3px;">${this.escapeHtml(material.containerLocation || '')}</td>`;
        html += `<td style="padding: 3px; text-align: right;">${(material.labWeight || 0).toFixed(3)}</td>`;
        html += '</tr>';
      });
    }
    html += '</tbody>';
    html += '</table>';
    html += '<table class="materials-table">';
    html += `
<colgroup>
  <col style="width:28%">
  <col style="width:12%">
  <col style=const d"width:18%">
  <col style="width:28%">
  <col style="width:14%">
</colgroup>`;
    html += '<thead>';

    html += `
<tr>
  <th colspan="5" class="materials-title">
    Liquid Materials
  </th>
</tr>`;
    html += '<tr>';
    html += '<th style="width:28%; text-align:center;">Material Name</th>';
    html += '<th style="width:12%; text-align:center;">Gal/Sk</th>';
    html += '<th style="width:18%; text-align:center;">Preferred Container</th>';
    html += '<th style="width:28%; text-align:center;">Container Location</th>';
    html += '<th style="width:14%; text-align:center;">Lab Weight</th>';
    html += '</tr>';
    html += '</thead>';
    html += '<tbody>';


    if (labSheetData.liquidMaterials.length === 0) {
      html += '<tr><td colspan="5" style=" padding: 3px; text-align: center;">No liquid materials</td></tr>';
    } else {
      labSheetData.liquidMaterials.forEach(material => {
        html += '<tr>';
        html += `<td style="padding: 3px;">${this.escapeHtml(material.materialName)}</td>`;
        html += `
<td style="padding: 3px; text-align: right;">
${material.galPerSack != null ? `${Number(material.galPerSack).toFixed(3)} gal/sk` : ''}
</td>`;
        html += `<td style="padding: 3px;">${this.escapeHtml(material.preferredContainer || '')}</td>`;
        html += `<td style="padding: 3px;">${this.escapeHtml(material.containerLocation || '')}</td>`;
        html += `<td style="padding: 3px; text-align: right;">${(material.labWeight || 0).toFixed(3)}</td>`;
        html += '</tr>';
      });
    }
    html += '</tbody>';
    html += '</table>';
    html += `
<div class="liquid-materials-separator"></div>
`;

    if (labSheetData.mixabilityScore == null) {
      html += `
  <table class="mixability-table">
      <tr>
          <td style="padding-right:20px;">
              <strong>Mixability</strong>
          </td>

          <td style="padding-right:20px;">
              Score:
          </td>

          <td style="padding-right:20px;">
              Time To Wet Solids (sec):
          </td>

          <td>
              Notes:
          </td>
      </tr>
  </table>
  `;
    }
     if (labSheetData.notes) {
      html += `
  <div class="notes-section">
      <strong>Notes:</strong>
      ${this.escapeHtml(labSheetData.notes)}
  </div>
  `;
    }
    html += this.buildTestsSectionsNew(testsToInclude, labSheetData);

    html += `
      </td>
    </tr>
  </tbody>
</table>
`;

return html;
  }

  private buildTestsSectionsNew(testsToInclude: TestItem[], labSheetData: SpacerLabSheetDto): string {
    let sections = '';

    testsToInclude.forEach(test => {
      switch (test.type) {
        case 'compatibility':
          sections += this.buildCompatibilitySectionNew(labSheetData);

          break;
        case 'qaqc':
          sections += this.buildQaqcSectionNew(labSheetData);
          break;
        case 'ssst':
          sections += this.buildSsstSectionNew(labSheetData);
          break;
        case 'hpht':
          sections += this.buildHphtSectionNew(test, labSheetData);
          break;
      }
    });
    return sections;
  }

  private buildCompatibilitySectionNew(labSheetData: SpacerLabSheetDto): string {
    const compat = labSheetData.spacerCompatibility;
    if (!compat) return '';

    return '';
//     let html = '<div style="margin-top: 15px; page-break-inside: avoid;">';
//     html += `
// <div style="
// margin-top:6px;
// margin-bottom:4px;
// "><strong>
// Spacer Compatibility
// </strong></div>`; html += '<div style="margin-top: 5px;">';
//     html += `
// <div>
//   Temperature:
//   <strong>${compat.temperature || 'N/A'}°F</strong>
// </div>

// <div>
//   Compatibility Project:
//   <strong>${compat.compatibilityProjectName || 'N/A'}</strong>
// </div>

// <div>
//   Compatibility Slurry:
//   <strong>${compat.compatibilitySlurryUfid || 'N/A'}</strong>
// </div>
// `;
//     html += `
// <table class="lab-table">
//   <tr>
//     <td class="qaqc-notes-label">
//       Notes:
//     </td>

//     <td class="qaqc-notes-value">
//       ${
//         compat.notes
//           ? `
//           <div class="qaqc-notes-highlight">
//             ${this.escapeHtml(compat.notes).replace(/\r?\n/g, '<br>')}
//           </div>
//           `
//           : '&nbsp;'
//       }
//     </td>
//   </tr>
// </table>
// `;
//     if (compat.ratios?.length) {

//       html += `
//   <table class="lab-table">

//   <thead>
//       <tr>
//           <th style="border:1px solid #000;padding:4px;">
//               Ratio
//           </th>
//       </tr>
//   </thead>
//   <tbody>
//   `;

//       compat.ratios.forEach(ratio => {
//         html += `
//       <tr>
//           <td style="border:1px solid #000;padding:4px;">
//               ${ratio.percentage}% ${this.convertRatioType(ratio.ratioType1)}
// :
// ${100 - ratio.percentage}% ${this.convertRatioType(ratio.ratioType2)}
//           </td>
//       </tr>`;
//       });

//       html += `
//   </tbody>
//   </table>`;
//     }
//     if (compat.mudContainerDeleted) {
//       html += `
//     <div style="color:red;font-weight:bold;margin-top:5px;">
//       Warning: Mud container has been deleted
//     </div>
//   `;
//     }
//     html += '</div>';
//     html += '</div>';
//     return html;
   }

  private buildQaqcSectionNew(labSheetData: SpacerLabSheetDto): string {
    const qaqc = labSheetData.spacerQaQc;
    if (!qaqc) return '';

    let html = '<div style="margin-top: 15px; page-break-inside: avoid;">';
    html += `
<div style="
margin-top:6px;
margin-bottom:4px;
"><strong>
Spacer QAQC
</strong></div>`; html += '<div style="margin-top: 5px;">';
    html += `
<div>
  Temperature:
  <strong>${qaqc.temperature || 'N/A'}°F</strong>
</div>
`;

    if (qaqc.notes) {
      html += `
  <table class="lab-table">
    <tr>
      <td class="qaqc-notes-label">
        Notes:
      </td>

      <td class="qaqc-notes-value">
  ${
    qaqc.notes
      ? `
      <div class="qaqc-notes-highlight">
        ${this.escapeHtml(qaqc.notes)
          .replace(/\r?\n/g, '<br>')}
      </div>
      `
      : '&nbsp;'
  }
</td>
    </tr>
  </table>
  `;
    }
    if (qaqc.readingTitles?.length) {

      qaqc.readingTitles.forEach(reading => {

        html += `
      <div style="margin-top:8px;font-weight:bold;">
        ${this.escapeHtml(reading.title)}
        ${reading.temperature != null
            ? ` @ ${reading.temperature}°F`
            : ''}
      </div>

      <table class="lab-table">
        <tr>
          <th>Lab/RPM</th>
          <th>600</th>
          <th>300</th>
          <th>200</th>
          <th>100</th>
          <th>60</th>
          <th>30</th>
          <th>20</th>
          <th>10</th>
          <th>6</th>
          <th>3</th>
          <th>Pv:cP</th>
          <th>Ty: lbf/100ft²</th>
        </tr>

        <tr>
          <td>CVX</td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
      </table>
    `;
      });

      html += `
    <table class="lab-table">
      <tr>
        <td>Started By</td>
        <td></td>
        <td>Stopped By</td>
        <td></td>
      </tr>

      <tr>
        <td>Comments</td>
        <td colspan="3"></td>
      </tr>
    </table>
  `;
    }
    html += '</div>';
    html += '</div>';
    return html;
  }

  private buildSsstSectionNew(labSheetData: SpacerLabSheetDto): string {
    const ssst = labSheetData.spacerSsst;
    if (!ssst) return '';

    let html = '<div style="margin-top: 15px; page-break-inside: avoid;">';
    html += `
<div style="
margin-top:6px;
margin-bottom:4px;
"><strong>Spacer SSST</strong>
</div>`;
    html += `<div style="margin-top: 5px;">Temperature: <strong>${ssst.temperature || 'N/A'}°F</strong></div>`;
    html += `
<table class="lab-table">
<tr>
<td colspan="8" style="border:1px solid #000;padding:4px;">
<strong>Notes:</strong>
${this.escapeHtml(ssst.notes || '')}
</td>
</tr>
</table>
`;
    html += `
<table class="lab-table">

<tr>
  <td style="width:120px;">Started By</td>
  <td style="width:60px;"></td>

  <td style="width:80px;">Stopped By</td>
  <td style="width:60px;"></td>
</tr>

<tr>
  <td>Conductivity Of Pure Spacer</td>
  <td></td>

  <td>Starting Mud Volume</td>
  <td></td>

  <td>Water Dispersion</td>
  <td>Yes / No</td>

  <td>Rod Cleaning Test</td>
  <td>Pass / Fail</td>
</tr>

<tr>
  <td>Comments</td>
  <td colspan="7"></td>
</tr>

</table>
`;
    html += `
<table class="lab-table">
<thead>
<tr>
<th>Spacer Added (ml)</th>
<th>Spacer (%)</th>
<th>Conductivity</th>
<th>Baseline</th>
</tr>
</thead>
<tbody>
`;
    for (let i = 0; i < 18; i++) {
      html += `
  <tr>
    <td>&nbsp;</td>
    <td>&nbsp;</td>
    <td>&nbsp;</td>
    <td>&nbsp;</td>
  </tr>
  `;
    }
    html += `
</tbody>
</table>
`;
    html += '</div>';
    return html;
  }

  private buildHphtSectionNew(test: TestItem, labSheetData: SpacerLabSheetDto): string {
    const hphtTests = labSheetData.spacerHpHtRheology || [];
    const hphtIndex = this.availableTests.filter(t => t.type === 'hpht').indexOf(test);
    const hpht = hphtTests[hphtIndex];

    if (!hpht) return '';

    let html = '<div style="margin-top: 15px; page-break-inside: avoid;">';
    html += `
<div style="
margin-top:6px;
margin-bottom:4px;
">
<strong>Spacer HPHT Rheology Test</strong>
</div>`;
    html += `<div style="margin-top: 5px;">Equipment: <strong>${hpht.equipment || 'N/A'}</strong></div>`;

    html += `
<table class="lab-table">
  <tr>
    <td class="qaqc-notes-label">
      Notes:
    </td>

    <td class="qaqc-notes-value">
  ${
    hpht.notes
      ? `
        <div class="qaqc-notes-highlight">
          ${this.escapeHtml(hpht.notes).replace(/\r?\n/g, '<br>')}
        </div>
      `
      : '&nbsp;'
  }
</td>
  </tr>
</table>
`;

    if (hpht.readings && hpht.readings.length > 0) {

      html += `
  <table class="lab-table hpht-table">

    <colgroup>
      <col style="width:200px">
      <col style="width:55px">
      <col style="width:60px">
      <col style="width:70px">

      <col style="width:40px">
      <col style="width:50px">
      <col style="width:60px">
      <col style="width:44px">

      <col style="width:55px">
      <col style="width:55px">

      <col style="width:40px">
      <col style="width:40px">

      <col style="width:55px">
      <col style="width:55px">

      <col style="width:60px">
      <col style="width:60px">
    </colgroup>

    <thead>
      <tr>
        <th>Reading Temp (°F)</th>
        <th>Reading Pressure (psi)</th>
        <th>Reading Time</th>
        <th></th>

        <th>600</th>
        <th>300</th>
        <th>200</th>
        <th>100</th>
        <th>60</th>
        <th>30</th>
        <th>20</th>
        <th>10</th>
        <th>6</th>
        <th>3</th>
        <th>10 sec</th>
        <th>10 min</th>
      </tr>
    </thead>

    <tbody>
  `;

      hpht.readings.forEach(reading => {

        html += `
      <tr>
        <td rowspan="2">${reading.temperature ?? ''}</td>
        <td rowspan="2">${reading.pressure ?? ''}</td>
        <td rowspan="2">${reading.time ?? ''}</td>

        <td>RampUp</td>

        <td></td>
<td></td>

<td colspan="2"></td>

<td colspan="2"></td>

<td></td>

<td colspan="2"></td>

<td colspan="3"></td>
      </tr>

      <tr>
        <td>RampDown</td>

        <td></td>
<td></td>

<td colspan="2"></td>

<td colspan="2"></td>

<td></td>

<td colspan="2"></td>

<td colspan="3"></td>
      </tr>
    `;
      });

      html += `
      <tr>
        <td>Started By</td>
        <td></td>

        <td>Stopped By</td>
        <td></td>

        <td>Test Date</td>
        <td></td>
        <td></td>

        <td>Equip. No.</td>

        <td colspan="8"></td>
      </tr>

      <tr>
        <td>Comments</td>
        <td colspan="15"></td>
      </tr>

    </tbody>
  </table>
  `;
    } else {
      html += '<div style="margin-top: 8px; font-style: italic;">No readings available</div>';
    }

    html += '</div>';

    return html;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  createBatch(): void {
    if (!this.spacerId) return;

    const selectedTests = this.availableTests.filter(t => t.selected);
    if (selectedTests.length === 0) {
      return;
    }

    const hasMudMeasurementTest =
      selectedTests.some(t =>
        t.type === 'compatibility' ||
        t.type === 'ssst'
      );

    const mudContainer = hasMudMeasurementTest
      ? (
        this.testInputsData?.spacerCompatibility?.mudCisProContainer ??
        this.testInputsData?.spacerSsst?.mudCisProContainer ??
        undefined
      )
      : undefined;

    const request: CreateSpacerBatchRequest = {
      testInputMasterIds: selectedTests.map(t => t.testInputMasterId),

      notes: undefined,
      batchLabVolume: undefined,

      measuredMudContainer: mudContainer,
      preferredMudContainer: mudContainer,

      mudContainerComments: undefined,
      replacePreferredMudContainer: false
    };
    this.spacerService.createBatch(this.spacerId, request).subscribe({
      next: (newBatch) => {
        this.existingBatches.push(newBatch);
        this.availableTests.forEach(t => t.selected = false);
        //this updates status, permissions and batch count
        this.reload();
        // Open the batch detail view instead of navigating
        this.openBatchDetail(newBatch);
      },
      error: (error) => {
        console.error('Error creating batch:', error);
        const errorMessage = error?.error?.detail || error?.message || 'Failed to create batch';
        alert(`Error: ${errorMessage}`);
      }
    });
  }

  // Open batch detail view (inline view, not navigation)
  openBatchDetail(batch: SpacerBatchDto): void {
    this.selectedBatch = batch;
    this.loadBatchTests(batch);
    this.showBatchDetail = true;
    this.batchDetailViewChange.emit(true);
  }

  // Load tests for the selected batch
  loadBatchTests(batch: SpacerBatchDto): void {
    if (!this.spacerId) return;

    this.spacerService.getTestInputs(this.spacerId).subscribe({
      next: (testInputs) => {
        this.processBatchTests(testInputs, batch);
      },
      error: (error) => {
        console.error('Error loading test inputs:', error);
      }
    });
  }

  private processBatchTests(testInputs: SpacerTestsDto, batch: SpacerBatchDto): void {
    this.batchTests = [];

    // Add Compatibility test
    if (testInputs.spacerCompatibility) {
      const isSelected = batch.tests.some(
        t => t.testInputMasterId === testInputs.spacerCompatibility!.testInputMasterId
      );

      this.batchTests.push({
        testInputMasterId: testInputs.spacerCompatibility.testInputMasterId,
        name: 'Spacer Compatibility',
        selected: isSelected,
        type: 'compatibility'
      });
    }

    // Add QAQC test
    if (testInputs.spacerQaQc) {
      const isSelected = batch.tests.some(
        t => t.testInputMasterId === testInputs.spacerQaQc!.testInputMasterId
      );

      this.batchTests.push({
        testInputMasterId: testInputs.spacerQaQc.testInputMasterId,
        name: 'Spacer QAQC',
        selected: isSelected,
        type: 'qaqc'
      });
    }

    // Add SSST test
    if (testInputs.spacerSsst) {
      const isSelected = batch.tests.some(
        t => t.testInputMasterId === testInputs.spacerSsst!.testInputMasterId
      );

      this.batchTests.push({
        testInputMasterId: testInputs.spacerSsst.testInputMasterId,
        name: 'Spacer SSST',
        selected: isSelected,
        type: 'ssst'
      });
    }

    // Add HPHT tests
    let fannCount = 0;
    let channCount = 0;

    (testInputs.spacerHpHtRheologyInputs || []).forEach((hpht) => {
      let currentNumber: number;

      switch (hpht.hpHtRheologyTypeId) {
        case SpacerHphtType.Fann77:
          currentNumber = ++fannCount;
          break;
        case SpacerHphtType.Chann7600:
        case SpacerHphtType.Chann7550:
          currentNumber = ++channCount;
          break;
        default:
          currentNumber = ++channCount;
          break;
      }

      const isSelected = batch.tests.some(
        t => t.testInputMasterId === hpht.testInputMasterId
      );

      this.batchTests.push({
        testInputMasterId: hpht.testInputMasterId,
        name: this.getHphtName(hpht.hpHtRheologyTypeId!, currentNumber),
        selected: isSelected,
        type: 'hpht'
      });
    });
  }

  // Close batch detail and go back to batches list
  closeBatchDetail(): void {
    this.showBatchDetail = false;
    this.selectedBatch = null;
    this.batchTests = [];
    this.batchDetailViewChange.emit(false);
  }

  get hasSelectedBatchTests(): boolean {
    return this.batchTests.some(t => t.selected);
  }

  // Handle double-click on batch row
  onBatchDoubleClick(batch: SpacerBatchDto): void {
    this.openBatchDetail(batch);
  }
  reload(): void {
    if(!this.spacerId) return;
    // whatever logic already runs in ngOnInit/ngOnChanges to fetch batches for this.spacerId
    this.loadBatches();
    //this updates status, permissions and batch count
    this.batchDetailViewChange.emit(false); // Reset the batch detail view state
  }

  get canCreateBatch(): boolean {
    if(!this.spacerStatus || this.projectStatus === 'Completed') {
      return false;
    }
    
    return this.spacerStatus === SlurryStatuses.ReadytoTest || 
    this.spacerStatus === SlurryStatuses.OngoinginLab ||
    this.spacerStatus === SlurryStatuses.SentToLabAgain;
  }

  get showCreateBatchSection(): boolean {
    console.log('canCreateBatch:', this.canCreateBatch, 'hasTests:', this.hasTests);
    return this.canCreateBatch && this.hasTests;
  }

  get canViewBatches(): boolean {
    if (!this.spacerId || this.projectStatus === 'Completed') {
      return false;
    }

    if (this.spacerStatus !== SlurryStatuses.TestCompleted) {
      return true;
    }

    return this.existingBatches.length > 0;
  }
}
